<template>
  <ContentWrap>
    <el-form :inline="true" :model="q" class="-mb-15px">
      <el-form-item label="订单号">
        <el-input v-model="q.orderNo" placeholder="订单号" clearable class="!w-200px" @keyup.enter="load" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="q.status" placeholder="全部" clearable class="!w-140px">
          <el-option label="待支付" :value="0" />
          <el-option label="已支付" :value="1" />
          <el-option label="已核销" :value="2" />
          <el-option label="已退款" :value="3" />
          <el-option label="已取消" :value="4" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="load"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="orderNo" label="订单号" width="170" />
      <el-table-column prop="scenicName" label="景区" min-width="150" show-overflow-tooltip />
      <el-table-column prop="ticketTierName" label="票档" min-width="140" show-overflow-tooltip />
      <el-table-column label="金额" width="110" align="center">
        <template #default="{ row }">¥{{ fenToYuan(row.payPrice) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="下单时间" min-width="160" align="center" />
      <el-table-column label="操作" width="150" align="center" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-button v-if="row.status === 1" link type="danger" @click="openRefund(row)">退款</el-button>
          <el-button v-if="row.status === 0" link type="warning" @click="cancel(row)">取消</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="q.pageNo" v-model:limit="q.pageSize" :total="total" @pagination="load" />
  </ContentWrap>

  <el-dialog v-model="detailVisible" title="订单详情" width="560px">
    <el-descriptions :column="2" border v-if="detail">
      <el-descriptions-item label="订单号">{{ detail.orderNo }}</el-descriptions-item>
      <el-descriptions-item label="景区">{{ detail.scenicName }}</el-descriptions-item>
      <el-descriptions-item label="票档">{{ detail.ticketTierName }}</el-descriptions-item>
      <el-descriptions-item label="金额">¥{{ fenToYuan(detail.payPrice) }}</el-descriptions-item>
      <el-descriptions-item label="联系人">{{ detail.contactName }}</el-descriptions-item>
      <el-descriptions-item label="手机">{{ detail.contactMobile }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusLabel(detail.status) }}</el-descriptions-item>
      <el-descriptions-item label="核销码">{{ detail.verifyCode }}</el-descriptions-item>
    </el-descriptions>
  </el-dialog>

  <Dialog v-model="refundVisible" title="订单退款" width="480px">
    <el-form :model="refundForm" label-width="100px">
      <el-form-item label="退款金额">
        <el-input-number v-model="refundForm.refundAmount" :min="0" :precision="2" class="!w-full" />
      </el-form-item>
      <el-form-item label="退款原因">
        <el-input v-model="refundForm.refundReason" type="textarea" :rows="3" placeholder="退款原因" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="refundVisible = false">取消</el-button>
      <el-button type="primary" :loading="refunding" @click="submitRefund">确认退款</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getScenicMerchantOrderPage,
  getScenicMerchantOrder,
  refundScenicMerchantOrder,
  cancelScenicMerchantOrder
} from '@/api/merchant/scenic'

defineOptions({ name: 'MerchantScenicOrder' })

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const q = reactive({ pageNo: 1, pageSize: 10, orderNo: '', status: undefined as number | undefined })
const detailVisible = ref(false)
const detail = ref<any>(null)
const refundVisible = ref(false)
const refunding = ref(false)
const refundForm = reactive<any>({})

const fenToYuan = (fen?: number) => (fen == null ? '-' : (fen / 100).toFixed(2))
const statusLabel = (s: number) => ({ 0: '待支付', 1: '已支付', 2: '已核销', 3: '已退款', 4: '已取消' }[s] || '-')
const statusTag = (s: number) =>
  ({ 0: 'warning', 1: 'success', 2: 'primary', 3: 'info', 4: 'info' } as Record<number, 'primary' | 'success' | 'warning' | 'info' | 'danger'>)[s] || 'info'

const load = async () => {
  loading.value = true
  try {
    const data = await getScenicMerchantOrderPage(q)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const openDetail = async (row: any) => {
  detail.value = await getScenicMerchantOrder(row.id)
  detailVisible.value = true
}

const openRefund = (row: any) => {
  Object.assign(refundForm, { id: row.id, refundAmount: row.payPrice ? row.payPrice / 100 : 0, refundReason: '' })
  refundVisible.value = true
}

const submitRefund = async () => {
  refunding.value = true
  try {
    await refundScenicMerchantOrder(refundForm.id, Math.round((refundForm.refundAmount || 0) * 100), refundForm.refundReason)
    ElMessage.success('退款成功')
    refundVisible.value = false
    load()
  } finally {
    refunding.value = false
  }
}

const cancel = async (row: any) => {
  await ElMessageBox.confirm('确认取消该订单？', '提示')
  await cancelScenicMerchantOrder(row.id)
  ElMessage.success('取消成功')
  load()
}

onMounted(load)
</script>
